import { queryOptions } from "@tanstack/react-query";

export const REFERRAL_ENABLED = import.meta.env.VITE_FEATURE_REFER_A_FRIEND === "true";
export const VIRAL_LOOPS_CAMPAIGN_ID = import.meta.env.VITE_VIRAL_LOOPS_CAMPAIGN_ID;

/** Viral Loops issues 7-character codes; mirrors the API's own bound. */
export const REFERRAL_CODE_MAX_LENGTH = 7;

/** Keep in step with the campaign's own reward setting. */
export const REFERRAL_REWARD_FALLBACK_EUROS = 7.5;

const VIRAL_LOOPS_API_URL = "https://app.viral-loops.com/api/v3";

/**
 * Injects the widget loader at runtime instead of from index.html: a static tag can
 * only use a build-time placeholder, but the Docker image builds without VITE_* vars,
 * so the campaign id would be empty in production. Injecting here reads the same env
 * as the rest of the app (import.meta.env, rewritten to window.env in builds) and
 * keeps the third-party script away from visitors when the feature is off.
 */
export function loadViralLoopsScript() {
  if (!REFERRAL_ENABLED || document.getElementById("viral-loops-loader")) return;

  const script = document.createElement("script");
  script.id = "viral-loops-loader";
  script.src = "https://app.viral-loops.com/widgetsV2/core/loader.js";
  script.async = true;
  script.dataset.campaignId = VIRAL_LOOPS_CAMPAIGN_ID;
  document.head.appendChild(script);
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// The loader assigns window.ViralLoops before its internal campaign registry is
// populated, so the first getCampaign() call can resolve undefined. Retry briefly.
export async function getReadyCampaign(viralLoops: NonNullable<Window["ViralLoops"]>) {
  for (const attemptDelay of [0, 300, 600, 1200]) {
    if (attemptDelay) await delay(attemptDelay);
    const campaign = await viralLoops.getCampaign();
    if (campaign) return campaign;
  }
  throw new Error("Viral Loops-campagne is niet beschikbaar.");
}

export type ReferralStats = {
  referralCode: string;
  /** Everyone who signed up through this participant's link. */
  invited: number;
  /** Referrals Viral Loops counts as converted — the ones that earn a reward. */
  approved: number;
};

/** Beyond this many referrals the counts stop growing; nobody is near it yet. */
const REFERRAL_PAGE_LIMIT = 100;

type ParticipantDataResponse = { referralCode?: string; referredLeads?: number };

async function getJson(path: string, params: Record<string, string>) {
  const query = new URLSearchParams({
    publicToken: VIRAL_LOOPS_CAMPAIGN_ID,
    ...params,
  });
  const response = await fetch(`${VIRAL_LOOPS_API_URL}${path}?${query}`);

  if (!response.ok) {
    throw new Error(`Viral Loops gaf status ${response.status}`);
  }

  return response.json() as Promise<unknown>;
}

/**
 * Reads the participant's own standings straight from Viral Loops. Their API
 * allows any origin and accepts the public campaign token, so this needs no
 * detour through our own backend.
 *
 * Conversions are counted from the referrals list, not from `referralCountTotal`.
 * That field mirrors the free-standing "Successful Referrals" number in Viral
 * Loops' dashboard, which has +/- buttons an admin can nudge — it read 1 on a
 * campaign whose successful-referral list was empty. Since this figure decides
 * what we tell someone they earned, it follows the list.
 */
export async function fetchReferralStats(email: string): Promise<ReferralStats> {
  const participant = (await getJson("/campaign/participant/data", {
    email,
  })) as ParticipantDataResponse;
  const referralCode = participant.referralCode ?? "";

  if (!referralCode) {
    return { referralCode, invited: 0, approved: 0 };
  }

  const approved = await getJson("/campaign/participant/referrals", {
    referralCode,
    limit: String(REFERRAL_PAGE_LIMIT),
    conversionStatus: "1",
  });

  // Rather show no standings than a confident €0,00 built on a shape we cannot read.
  if (!Array.isArray(approved)) {
    throw new Error("Viral Loops gaf een onverwachte referrals-respons.");
  }

  return {
    referralCode,
    invited: participant.referredLeads ?? 0,
    approved: approved.length,
  };
}

export const referralStatsQueryOptions = (email: string) =>
  queryOptions({
    queryKey: ["viral-loops", "stats", email],
    queryFn: () => fetchReferralStats(email),
    staleTime: 1000 * 60 * 5,
  });

// Anything but a bare amount ("7,50", "€7,50") falls back: scraping digits out of
// free text turns "2x €7,50" into €27,50 and promises money we do not pay.
const BARE_AMOUNT = /^\s*€?\s*(\d+(?:[.,]\d{1,2})?)\s*$/;

function parseReward(referrerReward: unknown): number {
  const match =
    typeof referrerReward === "string" ? BARE_AMOUNT.exec(referrerReward) : null;
  const amount =
    typeof referrerReward === "number"
      ? referrerReward
      : match
        ? Number.parseFloat(match[1].replace(",", "."))
        : NaN;

  return Number.isFinite(amount) && amount > 0 ? amount : REFERRAL_REWARD_FALLBACK_EUROS;
}

/**
 * `referrerReward` is a free-text field, so anything unreadable falls back.
 * Never rejects: the route loader awaits this, and the amount is not worth failing a page
 * over — ad blockers alone make app.viral-loops.com unreachable often enough.
 */
export async function fetchReferralReward(): Promise<number> {
  const query = new URLSearchParams({ publicToken: VIRAL_LOOPS_CAMPAIGN_ID });

  try {
    const response = await fetch(`${VIRAL_LOOPS_API_URL}/campaign/data?${query}`);
    if (!response.ok) throw new Error(`Viral Loops gaf status ${response.status}`);

    const { referrerReward } = (await response.json()) as { referrerReward?: unknown };
    return parseReward(referrerReward);
  } catch (error) {
    console.warn("Beloning ophalen bij Viral Loops mislukt", error);
    return REFERRAL_REWARD_FALLBACK_EUROS;
  }
}

export const referralRewardQueryOptions = () =>
  queryOptions({
    queryKey: ["viral-loops", "reward"],
    queryFn: fetchReferralReward,
    staleTime: 1000 * 60 * 60,
  });
